import { DocUploadProps, UploadMethods } from 'doc-upload';
import { getAuthHeader } from 'utils';
import { getEnv } from 'hooks/useEnv';
import { CountryCode, State } from 'types';
import { FormData } from 'components';
import { DocumentTypeInput } from './Fields';
import { match } from 'ts-pattern';

export type StateNeeded = Pick<State, 'appNum' | 'countryCode' | 'method' | 'channel'> & { source?: State['source'], flow?: State['flow'] };

export type ToDocUploadProps = (
  arg: {
    state: StateNeeded,
    formData: FormData,
    methodsRef: React.MutableRefObject<UploadMethods | undefined>
  }
) => DocUploadProps;

export const toDocUploadProps: ToDocUploadProps = ({ state, formData, methodsRef }) => {
  return {
    setAuthorizationHeaderValue,
    ...referenceFields(state, formData),
    ...documentFields(state, formData),
    ...businessFields(state, formData),
    onEvent: generateOnEvent(methodsRef),
    environment: process.env.REACT_APP_ENV === 'prod' ? 'production' :
      process.env.REACT_APP_ENV === 'dev' ? 'development' :
        'test',
    region: state.countryCode,
  };
}

async function setAuthorizationHeaderValue() {
  const overrideOktaToken = (getEnv('REACT_APP_ENV') === 'dev' && getEnv('REACT_APP_OKTA_TOKEN')) || undefined;
  const authHeader = getAuthHeader(overrideOktaToken);
  if (!authHeader) {
    throw new Error('Missing authorization header');
  }
  return authHeader;
}

function generateOnEvent(methodsRef: React.MutableRefObject<UploadMethods | undefined>): DocUploadProps['onEvent'] {
  return (evt) => {
    switch (evt.type) {
      case 'INIT':
        methodsRef.current = {
          startUpload: evt.data.startUpload,
        };
        break;
      default:
        break;
    }
  };
}

function referenceFields(state: StateNeeded, formData: FormData): Pick<DocUploadProps, 'reference' | 'referenceType'> {
  const referenceInput: string | undefined = formData?.state?.values?.reference;
  const reference = state.appNum || referenceInput || '';
  const referenceType = reference?.length === 13 ? 'APPLICATION_ID' as const : 'ACCOUNT_NUMBER' as const;

  return {
    reference,
    referenceType,
  };
}

function documentFields(state: StateNeeded, formData: FormData): Pick<DocUploadProps, 'documentType' | 'documentSubType'> {
  const documentTypeInput: DocumentTypeInput = formData?.state?.values?.documentType;

  if (documentTypeInput) {
    return {
      documentType: documentTypeInput,
      documentSubType: 'OTHER',
    } as const;
  }

  return match(state)
    .with({ flow: 'additional-card-holder-identity-verification' }, () => {
      return {
        documentType: 'OTHER',
        documentSubType: 'OTHER',
      } as const;
    })
    .with({ flow: 'additional-card-holder-upload-documents' }, () => {
      return {
        documentType: 'OTHER',
        documentSubType: 'OTHER',
      } as const;
    })
    .with({ source: 'apply-nz', method: 'DOCUMENTS' }, () => {
      // Note, technically we are not sure if user is doing POI or POA.
      // But business (@joel) has made the decision to always use POA for Apply NZ when uploading non-payslip documents.
      return {
        documentType: 'PROOF_OF_ADDRESS',
        documentSubType: 'OTHER',
      } as const;
    })
    .with({ method: 'PAYSLIPS' }, () => {
      return {
        documentType: 'PROOF_OF_INCOME',
        documentSubType: 'PAYSLIP',
        content: {
          upload: {
            fileRules: '<small>PDF, PNG, JPG, BMP, TIF and TIFF file formats.<br />Total upload limit: 22 MB (22,528 KB).</small>',
          },
        },
      } as const;
    })
    .with({ method: 'BANK_STATEMENTS' }, () => {
      return {
        documentType: 'PROOF_OF_INCOME',
        documentSubType: 'BANK_STATEMENT',
        validationRules: {
          maxFiles: 15,
          maxFileSize: 5 * 1024 * 1024,
          maxTotalSize: 22 * 1024 * 1024,
          allowedFileTypes: ['pdf'],
        },
        content: {
          upload: {
            fileRules: '<small>PDF format only. Max file size: 5 MB (5,242 KB) each. <br />Total upload limit: 22 MB (22,528 KB).</small>',
          },
        },
      } as const;
    })
    .with({ method: 'DOCUMENTS' }, () => {
      return {
        documentType: 'PROOF_OF_INCOME',
        documentSubType: 'OTHER',
      } as const;
    })
    .otherwise(() => {
      throw new Error(`Unexpected document type. state.flow: ${state.flow}, state.method: ${state.method}, documentTypeInput: ${documentTypeInput}, state.source: ${state.source}`);
    });
}

function businessFields(state: StateNeeded, formData: FormData): Pick<DocUploadProps, 'businessUnit' | 'clientId' | 'channel' | 'function'> {
  const businessUnit = 'LPAY';
  const instoreChannel = state.channel?.toLowerCase() === 'instore' ? 'INSTORE' : null;
  const documentTypeInput: DocumentTypeInput = formData?.state?.values?.documentType;

  return match(state)
    .with({ flow: 'additional-card-holder-identity-verification' }, () => {
      return {
        businessUnit,
        clientId: (state.countryCode === CountryCode.NZ) ? 'CREDIT_LIMIT_INCREASE_NZ' : 'CREDIT_LIMIT_INCREASE_AU', // TODO: We are not using SERVICE_X because they won't notify Activate. See https://l-fs.slack.com/archives/C08JREPGHD5/p1754450539137669?thread_ts=1754364787.278419&cid=C08JREPGHD5
        channel: 'ONLINE',
        function: 'ADDITIONAL_CARD_HOLDER',
      } as const;
    })
    .with({ flow: 'additional-card-holder-upload-documents' }, () => {
      return {
        businessUnit,
        clientId: (state.countryCode === CountryCode.NZ) ? 'CREDIT_LIMIT_INCREASE_NZ' : 'CREDIT_LIMIT_INCREASE_AU', // TODO: We are not using SERVICE_X because they won't notify Activate. See https://l-fs.slack.com/archives/C08JREPGHD5/p1754450539137669?thread_ts=1754364787.278419&cid=C08JREPGHD5
        channel: 'ONLINE',
        function: 'ADDITIONAL_CARD_HOLDER',
      } as const;
    })
    .with({ source: 'website-au' }, () => {
      return {
        businessUnit,
        clientId: (state.method === 'DOCUMENTS' && documentTypeInput === 'OTHER') ? 'SERVICING_AU' : 'ORIGINATIONS_AU',
        channel: instoreChannel || 'WEBSITE',
        function: 'OTHER',
      } as const;
    })
    .with({ source: 'website-nz' }, () => {
      return {
        businessUnit,
        clientId: (state.method === 'DOCUMENTS' && documentTypeInput === 'OTHER') ? 'SERVICING_NZ' : 'ORIGINATIONS_NZ',
        channel: instoreChannel || 'WEBSITE',
        function: 'OTHER',
      } as const;
    })
    .with({ source: 'smp-au' }, () => {
      return {
        businessUnit,
        clientId: 'PARTNER_AU',
        channel: 'INSTORE',
        function: 'NEW_APPLICATION',
      } as const;
    })
    .with({ source: 'smp-nz' }, () => {
      return {
        businessUnit,
        clientId: 'PARTNER_NZ',
        channel: 'INSTORE',
        function: 'NEW_APPLICATION',
      } as const;
    })
    .with({ source: 'cli-au' }, () => {
      // LSC CLI AU actually has embedded Doc Uploader in the web app.
      // So that we don't expect this source to be used for Income LFS.
      // However, we will support it just in case.
      return {
        businessUnit,
        clientId: 'CREDIT_LIMIT_INCREASE_AU',
        channel: instoreChannel || 'ONLINE',
        function: 'CREDIT_LIMIT_INCREASE',
      } as const;
    })
    .with({ source: 'cli-nz' }, () => {
      // LSC CLI NZ actually has embedded Doc Uploader in the web app.
      // So that we don't expect this source to be used for Income LFS.
      // However, we will support it just in case.
      return {
        businessUnit,
        clientId: 'CREDIT_LIMIT_INCREASE_NZ',
        channel: instoreChannel || 'ONLINE',
        function: 'CREDIT_LIMIT_INCREASE',
      } as const;
    })
    .with({ source: 'apply-au' }, () => {
      return {
        businessUnit,
        clientId: 'ORIGINATIONS_AU',
        channel: instoreChannel || 'ONLINE',
        function: 'NEW_APPLICATION',
      } as const;
    })
    .with({ source: 'apply-nz' }, () => {
      return {
        businessUnit,
        clientId: 'ORIGINATIONS_NZ',
        channel: instoreChannel || 'ONLINE',
        function: 'NEW_APPLICATION',
      } as const;
    })
    .otherwise(() => {
      throw new Error(`Unexpected source. state.flow: ${state.flow}, state.source: ${state.source}`);
    });
}
